/**
 * Flight operations · the flight record (AODB).
 *
 * The airport's own record of every movement, and what makes it better than
 * a record that only stores what it is told:
 *
 *  - **It predicts.** Beside scheduled, estimated and actual sits a predicted
 *    time for every flight not yet flown — learned from that service's own
 *    history, corrected for how the airline's estimates turn out, and held
 *    back by a late inbound unit plus the turnaround the airline really
 *    achieves. Every prediction says why, in words, and how sure it is.
 *  - **It runs itself.** The day is generated from the seasonal schedule
 *    (SSIM), and movement messages (MVT) are applied as they arrive — the
 *    times, the milestones, the load, the delay codes — without anyone typing.
 *  - **It checks itself.** A message that would put take-off before push-back
 *    is held with the reason; an estimate that passed with nobody reporting,
 *    or more on board than seats, is flagged on the flight.
 *  - **It remembers.** Every change — by whom, from where, from what to what
 *    — is on the flight's history, and two people editing cannot overwrite
 *    each other.
 */
import { useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import {
  useAodbChange,
  useAodbDay,
  useAodbGenerate,
  useAodbImport,
  useAodbMessages,
  useAodbMovement,
  useAodbPostMessage,
  useAodbSchedule,
  useAodbSummary,
} from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
} from "../../design/components";
import { STATUS_LABEL, type Movement, type MovementStatus } from "../../domain/aodb";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { hhmm as clock } from "../../lib/format";
import { Tiles } from "../executive/shared";
import { FlightTheme } from "./FlightTheme";
import { ActualsForm, DelayCodesForm, LoadsForm, NewMovementCard, SourceNote, TobtForm } from "./FlightEntryForms";
import s from "./Flights.module.css";
import a from "./Aodb.module.css";

type Tab = "today" | "schedule" | "messages";
const hhmm = (iso?: string) => clock(iso, "—");
const DAYS = ["", "M", "T", "W", "T", "F", "S", "S"];
/** actualAt / actual_at → "actual at", as the history names it */
const fieldWords = (f: string) => t(f.replace(/([A-Z])/g, " $1").replace(/_/g, " ").toLowerCase());

export function AodbPage() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("today");
  const summary = useAodbSummary();
  const sm = summary.data;

  useScreenContext({
    route: "/aodb",
    title: "Flight record",
    summary: sm
      ? [
          t("{n} flights on the record today, {f} flown.", { n: sm.movements, f: sm.flown }),
          sm.onTimePct != null ? t("{p}% on time so far.", { p: sm.onTimePct }) : t("Nothing has flown yet today."),
          t("{n} predicted to run late; {f} flagged.", { n: sm.predictedLate.length, f: sm.flagged.length }),
        ]
      : [],
    offers: [
      { label: t("Which flights will run late?"), ask: "which flights are predicted to run late today, and why?" },
    ],
    worksInFull: true,
  });

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Flight record">
        <p className={s.lede}>
          {t(
            "The airport's record of every flight, with a predicted time for each one not yet flown.",
          )}
        </p>
        {sm && (
          <Tiles
            currency="SAR"
            items={[
              {
                key: "flights",
                label: t("flights today"),
                value: sm.movements,
                unit: "",
                text: t("{a} arrivals · {d} departures", { a: sm.arrivals, d: sm.departures }),
                sources: [],
              },
              {
                key: "otp",
                label: t("on time"),
                value: sm.onTimePct,
                unit: "%",
                text: t("within 15 min, of {n} flown", { n: sm.flown }),
                tone: sm.onTimePct == null ? undefined : sm.onTimePct >= 80 ? "good" : "warn",
                sources: [],
              },
              {
                key: "delay",
                label: t("average delay"),
                value: sm.averageDelayMin,
                unit: t("min"),
                text: t("against the schedule"),
                sources: [],
              },
              {
                key: "late",
                label: t("predicted late"),
                value: sm.predictedLate.length,
                unit: "",
                text: t("more than 15 min, not yet flown"),
                tone: sm.predictedLate.length ? "warn" : "good",
                sources: [],
              },
              {
                key: "flagged",
                label: t("flagged"),
                value: sm.flagged.length,
                unit: "",
                text: t("contradicting the record"),
                tone: sm.flagged.length ? "bad" : "good",
                sources: [],
              },
              {
                key: "load",
                label: t("on board"),
                value: sm.loadActual,
                unit: "",
                text: t("reported by the airlines"),
                sources: [],
              },
            ]}
          />
        )}
        <Segmented
          ariaLabel="Flight record"
          value={tab}
          onChange={setTab}
          options={[
            { value: "today", label: "Today" },
            { value: "schedule", label: "Seasonal schedule" },
            { value: "messages", label: "Messages" },
          ]}
        />
        {tab === "today" && <Today />}
        {tab === "schedule" && <Schedule />}
        {tab === "messages" && <Messages />}
      </Page>
    </FlightTheme>
  );
}

function ConfidenceChip({ c }: { c: "high" | "medium" | "low" }) {
  return (
    <span className={a.conf} data-c={c}>
      {t(c)}
    </span>
  );
}

function Today() {
  const day = useAodbDay();
  const summary = useAodbSummary();
  const [open, setOpen] = useState<string | null>(null);
  const [dir, setDir] = useState<"all" | "arrival" | "departure">("all");
  const [adding, setAdding] = useState(false);
  if (day.isPending) return <Skeleton rows={8} />;
  if (day.isError) return <ErrorState error={day.error} retry={() => void day.refetch()} />;
  const rows = day.data.items.filter((m) => dir === "all" || m.direction === dir);
  const late = summary.data?.predictedLate ?? [];
  return (
    <>
      {late.length > 0 && (
        <Card
          title="Predicted to run late"
          sub="Flights still to come, and why each is predicted late"
          testId="aodb-late"
        >
          <ul className={a.late}>
            {late.map((l) => {
              const m = day.data.items.find((x) => x.id === l.id);
              return (
                <li key={l.id} data-testid={`late-${l.flight}`}>
                  <button type="button" className={a.link} onClick={() => setOpen(l.id)}>
                    <b>{l.flight}</b>
                  </button>{" "}
                  <span className={a.dim}>{t(l.direction)}</span>{" "}
                  <b className={a.plus}>
                    +{l.delayMinutes} {t("min")}
                  </b>{" "}
                  <ConfidenceChip c={l.confidence} />
                  {m?.prediction && <div className={a.basis}>{m.prediction.basis.join(" · ")}</div>}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      {adding && (
        <NewMovementCard
          onClose={() => setAdding(false)}
          onAdded={(m) => {
            setOpen(m.id);
            setDir("all");
          }}
        />
      )}
      <Card
        title="Today's flights"
        sub={t("Scheduled, estimated, actual, and predicted times")}
        actions={
          <div className={a.row}>
            <Segmented
              ariaLabel="Direction"
              value={dir}
              onChange={setDir}
              options={[
                { value: "all", label: "All" },
                { value: "arrival", label: "Arrivals" },
                { value: "departure", label: "Departures" },
              ]}
            />
            <Button size="sm" onClick={() => setAdding(true)} data-testid="aodb-add">
              {t("Add a flight")}
            </Button>
          </div>
        }
        testId="aodb-day"
      >
        {rows.length === 0 ? (
          <EmptyState>
            {day.data.covered
              ? t("No flights on the record for this day.")
              : t("No schedule is loaded for this day.")}
          </EmptyState>
        ) : (
          <div className={a.scroll}>
            <table className={a.table}>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th>{t("From / to")}</th>
                  <th>{t("Scheduled")}</th>
                  <th>{t("Estimated")}</th>
                  <th>{t("Actual")}</th>
                  <th>{t("Predicted")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Reg.")}</th>
                  <th>{t("Load")}</th>
                  <th>{t("Checks")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr
                    key={m.id}
                    data-testid={`mv-${m.carrier}${m.number}-${m.direction}`}
                    onClick={() => setOpen(m.id)}
                    className={open === m.id ? a.on : undefined}
                  >
                    <td>
                      <b>
                        {m.carrier}
                        {m.number}
                      </b>{" "}
                      <span className={a.dim}>{m.direction === "arrival" ? "↓" : "↑"}</span>
                    </td>
                    <td>{m.counterpartNode ?? "—"}</td>
                    <td className={a.t}>{hhmm(m.scheduledAt)}</td>
                    <td className={a.t}>{hhmm(m.estimatedAt)}</td>
                    <td className={a.t}>
                      {hhmm(m.actualAt)}
                      {m.actualAt && m.provenance?.actual_at?.source === "manual" && (
                        <small className={a.dim}> {t("by hand")}</small>
                      )}
                    </td>
                    <td className={a.t}>
                      {m.predictedAt && m.prediction ? (
                        <>
                          {hhmm(m.predictedAt)}{" "}
                          <small className={m.prediction.delayMinutes > 15 ? a.plus : a.dim}>
                            {m.prediction.delayMinutes >= 0 ? "+" : ""}
                            {m.prediction.delayMinutes}
                          </small>{" "}
                          <ConfidenceChip c={m.prediction.confidence} />
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className={a.status} data-s={m.status}>
                        {t(STATUS_LABEL[m.status ?? "scheduled"])}
                      </span>
                    </td>
                    <td className={a.t}>{m.registration ?? "—"}</td>
                    <td className={a.t}>
                      {m.loads?.actual ?? m.loads?.booked ?? "—"}
                      {m.capacity ? `/${m.capacity}` : ""}
                    </td>
                    <td>
                      {m.flags.length ? (
                        <span className={a.flag} title={m.flags.map((f) => f.why).join("; ")}>
                          ⚠ {m.flags.length}
                        </span>
                      ) : (
                        <span className={a.ok}>✓</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <MovementPanel id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function MovementPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useAodbMovement(id);
  const change = useAodbChange();
  const [est, setEst] = useState("");
  const [gate, setGate] = useState("");
  const [status, setStatus] = useState<MovementStatus | "">("");
  const [note, setNote] = useState("");
  if (q.isPending) return <Skeleton rows={5} />;
  if (q.isError) return <ErrorState error={q.error} />;
  const { movement: m, history, messages } = q.data;
  const save = () => {
    const changes: Partial<Movement> = {};
    if (est) {
      const base = new Date(m.scheduledAt ?? Date.now());
      const [hh, mm] = est.split(":").map(Number);
      const local = new Date(base.getTime() + 3 * 3_600_000);
      local.setUTCHours(hh, mm, 0, 0);
      changes.estimatedAt = new Date(local.getTime() - 3 * 3_600_000).toISOString();
    }
    if (gate) changes.handling = { ...m.handling, gate };
    if (status) changes.status = status;
    change.mutate(
      { id: m.id, changes, version: m.version, note: note || undefined },
      {
        onSuccess: () => {
          setEst("");
          setGate("");
          setStatus("");
          setNote("");
        },
      },
    );
  };
  return (
    <Card
      title={`${m.carrier}${m.number} · ${t(m.direction)} · ${m.serviceDate}`}
      sub={`${m.counterpartNode ?? ""} · ${m.classKey ?? ""} · ${m.registration ?? t("registration not yet known")} · ${t("version {v}", { v: m.version })}`}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
      testId="aodb-panel"
    >
      <div className={a.panel}>
        <div>
          <h3 className={a.h3}>{t("Times")}</h3>
          <dl className={a.kv}>
            <dt>{t("Scheduled")}</dt>
            <dd>{hhmm(m.scheduledAt)}</dd>
            <dt>{t("Estimated")}</dt>
            <dd>{hhmm(m.estimatedAt)}</dd>
            <dt>{t("Actual")}</dt>
            <dd>
              {hhmm(m.actualAt)} <SourceNote p={m.provenance?.actual_at} testId="aodb-actual-source" />
            </dd>
            {Object.entries(m.milestones).map(([k, v]) => (
              <span key={k} style={{ display: "contents" }}>
                <dt>{t(k.replace("_", " "))}</dt>
                <dd>
                  {hhmm(v)} <SourceNote p={m.provenance?.[`milestones.${k}`]} />
                </dd>
              </span>
            ))}
            <dt>{t("Predicted")}</dt>
            <dd>
              {m.predictedAt ? (
                <>
                  {hhmm(m.predictedAt)} {m.prediction && <ConfidenceChip c={m.prediction.confidence} />}
                </>
              ) : (
                "—"
              )}
            </dd>
          </dl>
          {m.prediction && (
            <div className={a.why} data-testid="aodb-why">
              <b>{t("Why this prediction")}</b>
              <ul>
                {m.prediction.basis.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <small className={a.dim}>
                {t("{model} · {n} past flights behind it", { model: m.prediction.model, n: m.prediction.sample })}
              </small>
            </div>
          )}
          {m.flags.length > 0 && (
            <div className={a.flags}>
              {m.flags.map((f) => (
                <div key={f.why}>⚠ {f.why}</div>
              ))}
            </div>
          )}
          <h3 className={a.h3}>{t("Load and delay")}</h3>
          <dl className={a.kv}>
            <dt>{t("Booked")}</dt>
            <dd>{m.loads?.booked ?? "—"}</dd>
            <dt>{t("On board")}</dt>
            <dd>
              {m.loads?.actual ?? "—"}
              {m.capacity ? ` / ${m.capacity}` : ""} <SourceNote p={m.provenance?.["loads.actual"]} />
            </dd>
            {m.loads?.bags != null && (
              <>
                <dt>{t("Bags")}</dt>
                <dd>{m.loads.bags}</dd>
              </>
            )}
            {m.loads?.cargo_kg != null && (
              <>
                <dt>{t("Cargo (kg)")}</dt>
                <dd>{m.loads.cargo_kg}</dd>
              </>
            )}
            <dt>{t("Delay codes")}</dt>
            <dd>
              {m.delayCodes.length
                ? m.delayCodes.map((c) => `${c.code}${c.minutes ? ` (${c.minutes} min)` : ""}`).join(", ")
                : "—"}{" "}
              <SourceNote p={m.provenance?.delay_codes} />
            </dd>
            <dt>{t("Gate / stand")}</dt>
            <dd>{[m.handling.gate, m.handling.position].filter(Boolean).join(" / ") || "—"}</dd>
          </dl>
        </div>
        <div>
          <h3 className={a.h3}>{t("Change the record")}</h3>
          <div className={a.form}>
            <Field label="Estimated (local HH:MM)">
              <Input value={est} onChange={(e) => setEst(e.target.value)} placeholder="14:35" data-testid="aodb-est" />
            </Field>
            <Field label="Gate">
              <Input value={gate} onChange={(e) => setGate(e.target.value)} />
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as MovementStatus)}>
                <option value="">—</option>
                {(Object.keys(STATUS_LABEL) as MovementStatus[]).map((k) => (
                  <option key={k} value={k}>
                    {t(STATUS_LABEL[k])}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Why">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("the airline's call, the handler …")}
              />
            </Field>
            <Button
              variant="primary"
              onClick={save}
              disabled={change.isPending || (!est && !gate && !status)}
              data-testid="aodb-save"
            >
              {t("Save to the record")}
            </Button>
            {change.isError && <p className={a.bad}>{String(change.error)}</p>}
          </div>
          <div className={a.entries} data-testid="aodb-entries">
            {m.status !== "cancelled" && <ActualsForm key={`act-${m.id}`} m={m} />}
            <LoadsForm key={`load-${m.id}`} m={m} />
            <DelayCodesForm key={`code-${m.id}`} m={m} />
            {m.direction === "departure" && m.status !== "cancelled" && !m.actualAt && (
              <div>
                <h3 className={a.h3}>{t("Committed off-block")}</h3>
                <p className={a.dim}>
                  {m.milestones.tobt
                    ? t("Committed: {t}. A new time replaces it; the history keeps both.", { t: hhmm(m.milestones.tobt) })
                    : t("No committed time yet.")}
                </p>
                <TobtForm movementId={m.id} serviceDate={m.serviceDate} near={m.scheduledAt} version={m.version} />
              </div>
            )}
          </div>
          <h3 className={a.h3}>{t("History")}</h3>
          <ol className={a.hist}>
            {history.map((e, i) => (
              <li key={i}>
                <span className={a.t}>{hhmm(e.at)}</span> <b>{t(e.field.replace(/_/g, " "))}</b>{" "}
                <span className={a.src} data-src={e.source}>
                  {t(e.source)}
                </span>
                {e.by ? <span className={a.dim}> · {e.by}</span> : null}
                {e.note ? <div className={a.dim}>{e.note}</div> : null}
              </li>
            ))}
          </ol>
          {messages.length > 0 && (
            <>
              <h3 className={a.h3}>{t("Messages that set it")}</h3>
              {messages.map((x) => (
                <pre key={x.id} className={a.raw}>
                  {x.raw}
                </pre>
              ))}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function Schedule() {
  const lines = useAodbSchedule();
  const imp = useAodbImport();
  const gen = useAodbGenerate();
  const [ssim, setSsim] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const week = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  return (
    <>
      <Card
        title="Seasonal schedule"
        sub="The seasonal schedule lines"
        testId="aodb-schedule"
        actions={
          <Button
            size="sm"
            onClick={() =>
              gen.mutate(
                { from: today, to: week },
                {
                  onSuccess: (r) =>
                    setMsg(t("{c} flights made; {k} were already on the record.", { c: r.created, k: r.alreadyThere })),
                },
              )
            }
          >
            {t("Generate the next 7 days")}
          </Button>
        }
      >
        {msg && <p className={a.dim}>{msg}</p>}
        {lines.isPending ? (
          <Skeleton rows={6} />
        ) : (
          <div className={a.scroll}>
            <table className={a.table}>
              <thead>
                <tr>
                  <th>{t("Season")}</th>
                  <th>{t("Flight")}</th>
                  <th>{t("Days")}</th>
                  <th>{t("Local time")}</th>
                  <th>{t("From / to")}</th>
                  <th>{t("Type")}</th>
                  <th>{t("Valid")}</th>
                  <th>{t("Source")}</th>
                </tr>
              </thead>
              <tbody>
                {(lines.data ?? []).map((l) => (
                  <tr key={l.id}>
                    <td>{l.season ?? "—"}</td>
                    <td>
                      <b>
                        {l.carrier}
                        {l.number}
                      </b>{" "}
                      <span className={a.dim}>{l.direction === "arrival" ? "↓" : "↑"}</span>
                    </td>
                    <td className={a.t}>
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => (l.days.includes(d) ? DAYS[d] : "·")).join("")}
                    </td>
                    <td className={a.t}>{l.localTime}</td>
                    <td>{l.counterpartNode ?? "—"}</td>
                    <td>{l.classKey ?? "—"}</td>
                    <td className={a.t}>
                      {l.validFrom} → {l.validTo}
                    </td>
                    <td>{l.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card
        title="Import an SSIM file"
        sub="Import an SSIM schedule file"
      >
        <textarea
          className={a.area}
          value={ssim}
          onChange={(e) => setSsim(e.target.value)}
          placeholder="3 SV 14830101J..."
          rows={5}
          aria-label={t("SSIM records")}
        />
        <Button
          onClick={() =>
            imp.mutate(
              { ssim },
              { onSuccess: (r) => setMsg(t("{l} legs read, {n} lines added.", { l: r.legs, n: r.lines })) },
            )
          }
          disabled={!ssim.trim() || imp.isPending}
        >
          {t("Import")}
        </Button>
      </Card>
    </>
  );
}

function Messages() {
  const inbox = useAodbMessages();
  const post = useAodbPostMessage();
  const day = useAodbDay();
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<{ outcome: string; text: string } | null>(null);
  const sample = () => {
    const m = day.data?.items.find((x) => x.direction === "arrival" && !x.actualAt && x.status !== "cancelled");
    if (!m) return;
    const sched = new Date(m.scheduledAt!);
    const touch = new Date(sched.getTime() + 18 * 60_000);
    const block = new Date(touch.getTime() + 7 * 60_000);
    const hm = (d: Date) => d.toISOString().slice(11, 16).replace(":", "");
    setRaw(
      `MVT\n${m.carrier}${m.number}/${m.serviceDate.slice(8, 10)}.${(m.registration ?? "HZXXX").replace("-", "")}.MED\nAA${hm(touch)}/${hm(block)}\nPX${m.loads?.booked ?? 150}\nDL93/0018`,
    );
  };
  return (
    <>
      <Card
        title="Apply a movement message"
        sub="Paste a movement message (MVT)"
        testId="aodb-post"
      >
        <textarea
          className={a.area}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={6}
          aria-label={t("Movement message")}
          placeholder={"MVT\nSV1478/27.HZASA.MED\nAA1502/1510\nPX171"}
          data-testid="aodb-raw"
        />
        <div className={a.row}>
          <Button size="sm" onClick={sample}>
            {t("Fill in an example arrival")}
          </Button>
          <Button
            variant="primary"
            data-testid="aodb-apply"
            disabled={!raw.trim() || post.isPending}
            onClick={() =>
              post.mutate(
                { kind: "mvt", raw },
                {
                  onSuccess: (r) =>
                    setResult({
                      outcome: r.outcome,
                      text:
                        r.outcome === "applied"
                          ? t("Applied: {f}.", { f: r.changed.map(fieldWords).join(", ") })
                          : (r.refusal ?? r.outcome),
                    }),
                },
              )
            }
          >
            {t("Apply")}
          </Button>
        </div>
        {result && (
          <p className={a.result} data-outcome={result.outcome} data-testid="aodb-result">
            <b>{t(result.outcome)}</b> — {result.text}
          </p>
        )}
      </Card>
      <Card title="Messages in" sub="Messages received, and what each one changed">
        {inbox.isPending ? (
          <Skeleton rows={4} />
        ) : (
          <ul className={a.inbox}>
            {(inbox.data ?? []).map((x) => (
              <li key={x.id}>
                <div className={a.row}>
                  <span className={a.result} data-outcome={x.outcome}>
                    {t(x.outcome)}
                  </span>
                  <b>{x.movement ?? "—"}</b>
                  <span className={a.dim}>
                    {hhmm(x.receivedAt)} · {x.kind.toUpperCase()}
                  </span>
                </div>
                <pre className={a.raw}>{x.raw}</pre>
                {x.refusal && <div className={a.bad}>{x.refusal}</div>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
